"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFileUpload = exports.uploadBlobThroughStorage = exports.uploadFileThroughProxy = void 0;
const react_1 = require("react");
const getAuthHeaders_1 = require("./getAuthHeaders");
function normalizeApiBase(url) {
    return url.trim().replace(/\/+$/, '');
}
/**
 * Multipart upload through the API to R2 (same-origin — avoids needing R2 bucket CORS).
 * Use for any browser upload where the R2 bucket's CORS policy can't be relied on to allow
 * direct-to-R2 PUTs (e.g. customer-facing shop origins not yet added to the bucket's CORS rules).
 */
async function uploadFileThroughProxy(options) {
    const { apiBaseUrl, tokenKey, file, meta, signal, onUploadProgress } = options;
    const base = normalizeApiBase(apiBaseUrl);
    const token = (0, getAuthHeaders_1.getAuthToken)(tokenKey);
    const data = await new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', meta.fileType);
        if (meta.productId)
            formData.append('productId', meta.productId);
        const xhr = new XMLHttpRequest();
        const onAbort = () => {
            xhr.abort();
            reject(new DOMException('Aborted', 'AbortError'));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
        xhr.open('POST', `${base}/storage/upload-attachment`);
        if (token)
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onUploadProgress) {
                onUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
        };
        xhr.onload = () => {
            signal?.removeEventListener('abort', onAbort);
            let body = {};
            try {
                body = JSON.parse(xhr.responseText || '{}');
            }
            catch {
                /* ignore parse errors — fall through to status check below */
            }
            if (xhr.status >= 200 && xhr.status < 300 && body.success) {
                resolve(body);
            }
            else {
                reject(new Error(typeof body.error === 'string' ? body.error : `Upload failed with status ${xhr.status}`));
            }
        };
        xhr.onerror = () => {
            signal?.removeEventListener('abort', onAbort);
            reject(new Error('Upload network error'));
        };
        xhr.send(formData);
    });
    onUploadProgress?.(100);
    return {
        id: data.fileId,
        url: data.url,
        key: data.key,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        type: meta.fileType,
        status: 'ACTIVE',
    };
}
exports.uploadFileThroughProxy = uploadFileThroughProxy;
/**
 * Presign → PUT to R2 → confirm. Use from non-React code (e.g. dashboard upload helpers).
 */
async function uploadBlobThroughStorage(options) {
    const { apiBaseUrl, tokenKey, file, meta, signal, onUploadProgress } = options;
    const base = normalizeApiBase(apiBaseUrl);
    const presignRes = await fetch(`${base}/storage/presign`, {
        method: 'POST',
        headers: (0, getAuthHeaders_1.getAuthHeaders)(tokenKey),
        body: JSON.stringify({
            fileType: meta.fileType,
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
            productId: meta.productId,
            gameId: meta.gameId,
            orderId: meta.orderId,
        }),
        signal,
    });
    if (!presignRes.ok) {
        const status = presignRes.status;
        let errDetail = '';
        let missingEnvVars = [];
        try {
            const ct = presignRes.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
                const errBody = (await presignRes.json());
                errDetail = typeof errBody?.error === 'string' ? errBody.error : '';
                if (Array.isArray(errBody?.missingEnvVars)) {
                    missingEnvVars = errBody.missingEnvVars.filter((x) => typeof x === 'string');
                }
            }
            else {
                const t = await presignRes.text();
                errDetail = t && t.length < 500 ? t.trim().slice(0, 280) : '';
            }
        }
        catch {
            /* ignore parse errors — fall through */
        }
        const missingSuffix = missingEnvVars.length > 0 ? ` — missing on API: ${missingEnvVars.join(', ')}` : '';
        let msg;
        if (status === 503) {
            msg =
                errDetail ||
                    'Upload service unavailable — object storage (R2) is missing or misconfigured on the API server.';
            msg = `${msg}${missingSuffix}`;
        }
        else if (status === 502 || status === 504) {
            msg =
                'Upload gateway error (502/504) — the edge proxy sometimes reports this when the API returns no body or rejects quickly. Confirm R2 env vars on api.mango-business.solutions backend, restart PM2 (`mango-backend`), and reload nginx.';
            if (errDetail)
                msg = `${msg} (${errDetail})`;
            msg = `${msg}${missingSuffix}`;
        }
        else {
            msg = `${errDetail || 'Failed to get upload URL'}${missingSuffix}`;
        }
        throw new Error(msg);
    }
    const { uploadUrl, fileId } = (await presignRes.json());
    await new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const xhr = new XMLHttpRequest();
        const onAbort = () => {
            xhr.abort();
            reject(new DOMException('Aborted', 'AbortError'));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onUploadProgress) {
                onUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
        };
        xhr.onload = () => {
            signal?.removeEventListener('abort', onAbort);
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            }
            else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        };
        xhr.onerror = () => {
            signal?.removeEventListener('abort', onAbort);
            reject(new Error('Upload network error'));
        };
        xhr.send(file);
    });
    onUploadProgress?.(100);
    const confirmRes = await fetch(`${base}/storage/confirm/${fileId}`, {
        method: 'POST',
        headers: (0, getAuthHeaders_1.getAuthHeaders)(tokenKey),
        signal,
    });
    if (!confirmRes.ok) {
        const errBody = await confirmRes.json().catch(() => ({}));
        const msg = typeof errBody?.error === 'string' ? errBody.error : 'Upload confirmation failed';
        throw new Error(msg);
    }
    const data = (await confirmRes.json());
    if (!data.file) {
        throw new Error('Invalid confirm response');
    }
    return data.file;
}
exports.uploadBlobThroughStorage = uploadBlobThroughStorage;
/**
 * Presign → PUT to R2 → confirm. Requires `apiBaseUrl` with `/api/v1` and Bearer token in localStorage.
 */
function useFileUpload(options) {
    const { apiBaseUrl, tokenKey } = options;
    const [uploading, setUploading] = (0, react_1.useState)(false);
    const [progress, setProgress] = (0, react_1.useState)(0);
    const [error, setError] = (0, react_1.useState)(null);
    const upload = (0, react_1.useCallback)(async (file, meta, signal) => {
        setUploading(true);
        setProgress(0);
        setError(null);
        try {
            return await uploadBlobThroughStorage({
                apiBaseUrl,
                tokenKey,
                file,
                meta,
                signal,
                onUploadProgress: setProgress,
            });
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Upload failed';
            setError(message);
            throw err;
        }
        finally {
            setUploading(false);
        }
    }, [apiBaseUrl, tokenKey]);
    return { upload, uploading, progress, error };
}
exports.useFileUpload = useFileUpload;
